/**
 * JourneyInstanceContext - Journey instance state management
 *
 * Integrates with @tonnie/sdk for journey data.
 * Provides backward-compatible interface while using SDK hooks.
 *
 * Phase 5 hardening:
 * - transformSdkJourney maps ALL SDK fields (sponsorship, payment, expiration)
 * - localStorage persistence for active instance
 * - Optimistic updates on journey creation via query-cache subscription
 *
 * @migration This context wraps SDK journey hooks.
 * For new code, prefer using useCurrentJourney() and useJourneys() directly from @tonnie/sdk.
 */

import { journeyKeys, useCurrentJourney, useJourneys, type Journey } from '@tonnie/sdk';
import { useSelectMyJourney } from '@tonnie/sdk'; // L6 (F47 · D-5)
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import type { IJourneyInstance } from '../types/auth.types';
import { useAuth } from './AuthContext';

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY SWITCH REDIRECT POLICY
// ═══════════════════════════════════════════════════════════════════════
//
// Every user-initiated journey switch lands on `/dashboard`. This is a
// deliberately strong rule — stronger than "only redirect from hero
// deep-links" — and it solves a class of bugs we've fought repeatedly:
//
//   • Hero deep-links (assessment / setup / results) are scoped to a
//     SPECIFIC journey. Letting the destination journey render under
//     the same URL causes the lifecycle manager to remount, the
//     engine to fresh-start, and the host's onChatChange to fire
//     StartAssessment — silently materialising a phantom in_progress
//     row in a journey the user only wanted to LOOK at.
//   • Hero homepages (`/checkup`, `/care`, …) and non-hero pages
//     (`/settings`, `/billing`) read journey-keyed cache. After a
//     switch they refetch correctly, but the user often loses
//     orientation on what changed and where they are. Landing on the
//     dashboard makes the new journey's overall state visible at a
//     glance and re-establishes the journey-aware context.
//
// The redirect runs INSIDE `handleSetActiveInstance`, BEFORE the new
// `activeInstance` value is committed, so React batches the URL change
// + state change in the same commit. The destination route reconciles
// directly under the new journey — no transient render of the old
// route under the new journey, no engine instantiate, no race.
//
// Carve-outs (paths where staying put is more useful than redirecting):
//   • `/dashboard`        — already there, no-op
//   • `/journey/builder`  — wizard owns its own navigation
//   • `/login` / `/signup` / `/forgot-password` / `/auth/*` — auth
//     flows own their own navigation
//
// Programmatic switches (e.g. the journey-create mutation auto-
// selecting the new instance via the cache subscription) bypass
// `handleSetActiveInstance` entirely — they call
// `setLocalActiveInstance` directly, so this redirect policy doesn't
// touch them. Only the user-facing journey switcher routes through
// `handleSetActiveInstance`.
const JOURNEY_SWITCH_DESTINATION = '/dashboard';

const REDIRECT_CARVE_OUT_PREFIXES = [
  '/dashboard',
  '/journey/builder',
  '/login',
  '/signup',
  '/forgot-password',
  '/auth',
];

function shouldRedirectOnJourneySwitch(pathname: string): boolean {
  return !REDIRECT_CARVE_OUT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Re-export the IJourneyInstance type as JourneyInstance for backward compat
export type { IJourneyInstance as JourneyInstance } from '../types/auth.types';

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

const STORAGE_KEY = 'tonnie_active_journey_instance';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * A guard registered by an in-progress flow host (e.g. CheckUp
 * assessment via `<FlowLifecycleManager>`) so it can prompt the user
 * before a journey switch destroys its in-flight work. Resolve `true`
 * to allow the switch, `false` to block it.
 *
 * `intendedInstanceId` is the journey the user is trying to switch TO
 * (or `null` for "deselect"); guards may use it to render a more
 * specific prompt ("Save your work and switch to Journey X?") although
 * none currently do.
 */
export type JourneySwitchGuard = (intendedInstanceId: string | null) => Promise<boolean>;

interface IJourneyInstanceContextType {
  activeInstance: IJourneyInstance | null;
  /**
   * Switch the active journey. Async because it awaits all registered
   * `JourneySwitchGuard`s — if any returns `false`, the switch is
   * aborted and `activeInstance` does NOT change.
   *
   * Resolves to `true` if the switch went through, `false` if a guard
   * blocked it. Callers (e.g. the selector) can use the boolean to
   * close their dropdown only on success, or surface a "still working"
   * indication.
   */
  setActiveInstance: (instance: IJourneyInstance | null) => Promise<boolean>;
  /**
   * Register a switch guard. Returns an unregister function — host
   * components MUST call this on unmount or the guard will keep
   * blocking switches after the host is gone (memory leak + ghost
   * prompts). Typical use:
   *
   *   useEffect(() => registerSwitchGuard(myGuard), [registerSwitchGuard, myGuard]);
   */
  registerSwitchGuard: (guard: JourneySwitchGuard) => () => void;
  instances: IJourneyInstance[];
  isLoading: boolean;
  /**
   * The journeys list query terminally failed (after the SDK's retry policy
   * exhausted). Consumers (e.g. `JourneyInstanceGuard`) MUST distinguish this
   * from "the user genuinely has no journeys" — an errored load is NOT an
   * empty result, and rendering "Build a journey" on a transient failure is a
   * data-loss-shaped lie. `false` while loading or once data has arrived.
   */
  isError: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Transform an SDK Journey row into the rich IJourneyInstance the
 * frontend works with. Cornerstone refactor 2026-05: all gating now
 * flows from `fundingSource` + `lifecycleState` (no more access /
 * unlockMode / sponsorshipType conflation).
 *
 * Exported so every surface that lists journeys — the switcher through this
 * context, "My journeys" over its own query — draws the SAME shape and cannot
 * drift field by field.
 */
export function transformSdkJourney(
  sdkJourney: Journey | null | undefined
): IJourneyInstance | null {
  if (!sdkJourney) return null;

  // Build scope from heroesEnabled or default to all five.
  const heroes = sdkJourney.heroesEnabled;
  const scope: IJourneyInstance['scope'] = heroes
    ? {
        checkup: heroes.includes('checkup'),
        teleportal: heroes.includes('teleportal'),
        qultureup: heroes.includes('qultureup'),
        care: heroes.includes('care'),
        passport: heroes.includes('passport'),
      }
    : { checkup: true, teleportal: true, qultureup: true, care: true, passport: true };

  // Map scenario. Default to pre_immigration if missing/invalid.
  const scenarioMap: Record<string, IJourneyInstance['scenario']> = {
    pre_immigration: 'pre_immigration',
    already_abroad: 'already_abroad',
    relocation_only: 'relocation_only',
    integration_only: 'integration_only',
  };
  const scenario: IJourneyInstance['scenario'] =
    scenarioMap[sdkJourney.scenario ?? ''] ?? 'pre_immigration';

  // SDK's Journey type doesn't (yet) declare paymentInfo / sponsorName
  // — they're computed at the API layer. Read via cast.
  const raw = sdkJourney as unknown as Record<string, unknown>;

  return {
    id: sdkJourney.id,
    scenario,
    // origin_country can legitimately be NULL in the new model
    // (lifecycle='pending_setup' before onboarding completes).
    originCountry: sdkJourney.originCountry ?? null,
    // W2 §9.5 — was `|| 'PT'`, so a journey whose destination did not arrive
    // read as a Portugal journey everywhere downstream. The column is NOT NULL
    // and the platform's sentinel for "not chosen yet" is 'UNK' (H9), so say
    // that rather than inventing a country. Display maps it to a placeholder.
    destinationCountry: sdkJourney.destinationCountry ?? 'UNK',
    label: sdkJourney.label ?? null,
    scope,
    fundingSource: (sdkJourney.fundingSource ?? 'self_free') as IJourneyInstance['fundingSource'],
    lifecycleState: (sdkJourney.lifecycleState ?? 'draft') as IJourneyInstance['lifecycleState'],
    createdAt: sdkJourney.createdAt || new Date().toISOString(),
    activatedAt: sdkJourney.activatedAt ?? null,
    expiresAt: sdkJourney.expiresAt ?? undefined,
    sponsorName: raw.sponsorName as string | undefined,
    paymentInfo: raw.paymentInfo as IJourneyInstance['paymentInfo'] | undefined,
    // Every company, each with its relationship and tenant — the switcher's
    // names and badges and each "leave" target. (An earlier version of this
    // mapper dropped the relationship fields, so the badge never arrived.)
    companies: sdkJourney.companies ?? [],
    // Why it cannot be archived yet, as the server says it.
    archiveBlock: sdkJourney.archiveBlock ?? null,
    isPrimary: sdkJourney.isPrimary ?? false,
    tierBadge: sdkJourney.tierBadge,
    // ── end L6 region ──
  };
}

/**
 * Load stored instance ID from localStorage
 */
function loadStoredInstanceId(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as string;
    }
  } catch {
    // Ignore errors
  }
  return null;
}

/**
 * Save instance ID to localStorage
 */
function saveInstanceId(instanceId: string | null): void {
  try {
    if (instanceId) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(instanceId));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore errors
  }
}

// ═══════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════

const JourneyInstanceContext = createContext<IJourneyInstanceContextType | undefined>(undefined);

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════

export function JourneyInstanceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [localActiveInstance, setLocalActiveInstance] = useState<IJourneyInstance | null>(null);

  // Track if we've initialized from storage to prevent re-running
  const initializedRef = useRef(false);

  // SDK hooks for real journey data
  const {
    data: sdkJourneys,
    isLoading: _journeysLoading,
    isPending: journeysPending,
    isError: journeysIsError,
  } = useJourneys();
  const { data: sdkCurrentJourney, isLoading: _currentLoading } = useCurrentJourney();

  // Transform SDK journeys to local format — memoized to avoid re-renders
  const instances = useMemo<IJourneyInstance[]>(() => {
    const sdkInstances =
      (sdkJourneys?.map(transformSdkJourney).filter(Boolean) as IJourneyInstance[]) || [];
    // Use SDK data if available, otherwise fall back to user context
    return sdkInstances.length > 0 ? sdkInstances : user?.journeyInstances || [];
  }, [sdkJourneys, user?.journeyInstances]);

  // Use isPending (status === 'pending') instead of just isLoading.
  // isLoading is only true when the query is actively fetching (pending + fetching).
  // isPending stays true when the query is DISABLED (waiting for auth token)
  // which prevents the guard from passing before data arrives.
  const isLoading = journeysPending;

  // Determine active instance: SDK current journey > localStorage > first instance
  const activeInstance = useMemo<IJourneyInstance | null>(() => {
    // If we have a local selection, prefer it (user explicitly chose)
    if (localActiveInstance) {
      // Make sure it still exists in the list (may have been deleted)
      const stillExists = instances.find((i) => i.id === localActiveInstance.id);
      if (stillExists) return stillExists; // Return the fresh version from SDK
    }

    // SDK current journey as fallback
    if (sdkCurrentJourney) {
      return transformSdkJourney(sdkCurrentJourney);
    }

    // Default to first instance
    return instances[0] ?? null;
  }, [localActiveInstance, sdkCurrentJourney, instances]);

  // Track every change of `activeInstance` (the value consumers see) — so
  // we can prove whether a journey switch came from the user (via
  // setActiveInstance + guards) or from a context-level recompute (SDK
  // refetch, instance list change, etc). Issue 1 ("CoPilot starts in
  // the OTHER journey when I switch") needs this to distinguish "the
  // context flipped underneath us" from "the user clicked the switcher".
  const prevActiveInstanceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevActiveInstanceIdRef.current === (activeInstance?.id ?? null)) return;

    console.log(
      `[journey:switch] active changed from=${prevActiveInstanceIdRef.current ?? 'null'} ` +
        `to=${activeInstance?.id ?? 'null'} ` +
        `lifecycle=${activeInstance?.lifecycleState ?? 'null'} ` +
        `funding=${activeInstance?.fundingSource ?? 'null'} ` +
        `dest=${activeInstance?.destinationCountry ?? 'null'}`
    );
    prevActiveInstanceIdRef.current = activeInstance?.id ?? null;
  }, [activeInstance]);

  // Initialize from localStorage on mount (only once)
  useEffect(() => {
    if (initializedRef.current || instances.length === 0) return;

    const storedId = loadStoredInstanceId();
    if (storedId) {
      const instance = instances.find((i) => i.id === storedId);
      if (instance) {
        setLocalActiveInstance(instance);
        initializedRef.current = true;
        return;
      }
    }

    // No stored instance or stored instance not found — prefer the
    // first full-tier journey over a basic one. The tier is the SERVER's
    // (`tierBadge`, computed by `journeyTierBadge` in @tonnie/foundation, F47);
    // this used to re-derive it from `fundingSource`, a second copy of
    // `tierOfFunding` in the client.
    const defaultInstance = instances.find((i) => i.tierBadge?.tier === 'full') || instances[0];
    if (defaultInstance) {
      setLocalActiveInstance(defaultInstance);
      saveInstanceId(defaultInstance.id);
    }
    initializedRef.current = true;
  }, [instances]);

  // ── L6 region: one active-journey rule (F47 · D-5) ────────────────────────
  // The server's `is_primary` IS the active journey — the one hero access,
  // `GET /journeys/current` and the citizen's other device use. This device
  // follows it whenever it CHANGES (first load, a switch made on another
  // device, a promotion after an archive, a re-rank on acceptance), and a
  // switch made here is persisted to it below. The stored selection above
  // answers only while the list carries no primary.
  // `mutate` is stable across renders; the mutation RESULT object is not, and
  // depending on it would rebuild the context value on every render.
  const { mutate: persistSelection } = useSelectMyJourney();
  const serverPrimaryId = instances.find((i) => i.isPrimary)?.id ?? null;
  const syncedPrimaryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!serverPrimaryId || serverPrimaryId === syncedPrimaryRef.current) return;
    const primary = instances.find((i) => i.id === serverPrimaryId);
    if (!primary) return;
    syncedPrimaryRef.current = serverPrimaryId;
    setLocalActiveInstance(primary);
    saveInstanceId(primary.id);
    initializedRef.current = true;
  }, [serverPrimaryId, instances]);
  // ── end L6 region ──────────────────────────────────────────────────────────

  // Clear local instance when no instances are available
  useEffect(() => {
    if (instances.length === 0 && localActiveInstance) {
      setLocalActiveInstance(null);
      saveInstanceId(null);
    }
  }, [instances.length, localActiveInstance]);

  // Listen for journey creation mutations — auto-select newly created journey
  useEffect(() => {
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'success') {
        // When a journey create mutation succeeds, refetch and select the new one
        const data = event.mutation.state.data as Journey | undefined;
        if (data && data.id && data.originCountry && data.destinationCountry) {
          const newInstance = transformSdkJourney(data);
          if (newInstance) {
            setLocalActiveInstance(newInstance);
            saveInstanceId(newInstance.id);
            // Invalidate to get a fresh list
            queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
            queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
          }
        }
      }
    });
    return unsubscribe;
  }, [queryClient]);

  // ── Switch-guard registry ────────────────────────────────────────
  // Set-of-guards held in a ref so registration doesn't trigger a
  // re-render. Hosts call `registerSwitchGuard` from a useEffect on
  // mount and dispose with the returned unregister fn.
  const switchGuardsRef = useRef<Set<JourneySwitchGuard>>(new Set());
  const registerSwitchGuard = useCallback((guard: JourneySwitchGuard): (() => void) => {
    switchGuardsRef.current.add(guard);
    return () => {
      switchGuardsRef.current.delete(guard);
    };
  }, []);

  const handleSetActiveInstance = useCallback(
    async (instance: IJourneyInstance | null): Promise<boolean> => {
      console.log(
        `[journey:switch] setActive request from=${activeInstance?.id ?? 'null'} ` +
          `to=${instance?.id ?? 'null'} guards=${switchGuardsRef.current.size}`
      );
      // No-op fast path — switching to the same journey is always
      // allowed and skips guards (avoids spurious leave prompts when
      // the selector re-emits the current selection).
      if (instance?.id === activeInstance?.id) {
        console.log(`[journey:switch] noop (same journey ${activeInstance?.id ?? 'null'})`);
        return true;
      }

      // Run all registered guards in parallel. If ANY guard returns
      // false (or rejects) the switch is aborted — the user said
      // "stay" on the current journey, or a guard threw and we err on
      // the side of NOT discarding their work.
      const guards = Array.from(switchGuardsRef.current);
      if (guards.length > 0) {
        const intendedId = instance?.id ?? null;

        console.log('[CP:Journey] running switch guards', { count: guards.length, intendedId });
        const results = await Promise.allSettled(guards.map((g) => g(intendedId)));
        const blocked = results.some((r) => r.status !== 'fulfilled' || r.value !== true);

        console.log('[CP:Journey] guards resolved', {
          results: results.map((r) =>
            r.status === 'fulfilled' ? `allow=${r.value}` : `rejected:${String(r.reason)}`
          ),
          blocked,
        });
        if (blocked) {
          console.log('[CP:Journey] switch ABORTED by guard');
          return false;
        }
      }

      console.log('[CP:Journey] switch COMMITTED', {
        from: activeInstance?.id ?? null,
        to: instance?.id ?? null,
      });

      // ── Redirect to dashboard on journey switch ──────────────────
      // Every user-initiated switch lands on /dashboard, except for
      // the carve-outs (auth / onboarding / journey-builder / already-
      // on-dashboard). See `JOURNEY_SWITCH_DESTINATION` and
      // `REDIRECT_CARVE_OUT_PREFIXES` for the full rationale.
      //
      // Order matters: `setLocation` runs FIRST so Wouter queues the
      // URL change in the same React commit batch as the
      // `setLocalActiveInstance` below. The next render reconciles
      // against /dashboard with the new activeInstance — the previous
      // route never gets a chance to render under the new journey,
      // which is exactly the property that prevents lifecycle-manager
      // remount races, phantom StartAssessment calls, and stale
      // journey-keyed cache reads.
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (shouldRedirectOnJourneySwitch(currentPath)) {
          console.log('[CP:Journey] journey switch → redirect to dashboard', {
            from: currentPath,
            to: JOURNEY_SWITCH_DESTINATION,
          });
          setLocation(JOURNEY_SWITCH_DESTINATION);
        }
      }

      setLocalActiveInstance(instance);
      saveInstanceId(instance?.id ?? null);
      // ── L6 region: F47 · D-5 — the choice is persisted, not device-local ──
      // Every switch to a different journey, with no `isPrimary` shortcut: that
      // flag is the CACHED list's, and after a switch A→B it still says A until
      // the refetch lands — so switching back to A in that window would persist
      // nothing, the refetch would report B, and the follow effect above would
      // move the device back to B. The server answers a no-change select with
      // `changed: false` and writes nothing.
      if (instance) {
        persistSelection(instance.id);
      }
      // ── end L6 region ──
      return true;
    },
    [activeInstance?.id, setLocation, persistSelection]
  );

  // Memoize context value
  const contextValue = useMemo<IJourneyInstanceContextType>(
    () => ({
      activeInstance,
      setActiveInstance: handleSetActiveInstance,
      registerSwitchGuard,
      instances,
      isLoading,
      isError: journeysIsError,
    }),
    [
      activeInstance,
      handleSetActiveInstance,
      registerSwitchGuard,
      instances,
      isLoading,
      journeysIsError,
    ]
  );

  return (
    <JourneyInstanceContext.Provider value={contextValue}>
      {children}
    </JourneyInstanceContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useJourneyInstance() {
  const context = useContext(JourneyInstanceContext);
  if (context === undefined) {
    throw new Error('useJourneyInstance must be used within a JourneyInstanceProvider');
  }
  return context;
}
